import React, { useState } from 'react';
import {
  OTHER_OPTION_LABEL,
  TACTIC_TERM_MAX_LENGTH,
} from '../../../constants/coachTactics';

/**
 * The body shared by PLAYING STYLE, OFFENSIVE TACTICS and DEFENSIVE TACTICS.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * PlayingStyleSection.js and DefensiveTacticsSection.js were two byte-identical
 * 140-line components that differed only in their strings and their option
 * list. Adding a third copy for the offensive half, and then teaching all three
 * to accept a coach's own wording, would have meant maintaining the same logic
 * in three places — which is how the licence dropdown ended up drifting into
 * two different catalogues in the first place. The markup, class names and
 * props below are the originals, unchanged; only the duplication is gone. The
 * three section files stay where they are and keep their names and their
 * props, so no caller had to move.
 *
 * THE FREE-TEXT ESCAPE HATCH
 * --------------------------
 * Same shape as AddLicenseModal: an "Other" entry in the dropdown reveals a
 * text field, and the typed wording is what gets stored. Nothing validates
 * these strings server-side, so the two limits that matter are enforced right
 * here — a term is trimmed and capped at TACTIC_TERM_MAX_LENGTH, and it cannot
 * land twice. Matching for "twice" is case-insensitive and ignores surrounding
 * space: a coach who types "high press" gets the list's own "High Press" rather
 * than a second chip that reads the same to a human and differently to a
 * machine.
 */
const TacticSelectionSection = ({
  isEditing,
  values = [],
  onUpdate,
  options = [],
  title,
  addButtonLabel,
  emptyLabel,
  modalTitle,
  selectLabel,
  selectPlaceholder,
  removeAriaLabel,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedValue, setSelectedValue] = useState('');
  const [customValue, setCustomValue] = useState('');

  const isOther = selectedValue === OTHER_OPTION_LABEL;
  const normalise = (value) => (value || '').trim().toLowerCase();

  const closeModal = () => {
    setShowAddModal(false);
    setSelectedValue('');
    setCustomValue('');
  };

  /**
   * What the coach is actually adding. Free text is trimmed and, when it names
   * something the suggestion list already spells, replaced by that spelling so
   * the two never sit side by side in different casing.
   */
  const resolveEntry = () => {
    if (!isOther) return selectedValue;
    const typed = customValue.trim().slice(0, TACTIC_TERM_MAX_LENGTH);
    if (!typed) return '';
    const known = options.find((option) => normalise(option) === normalise(typed));
    return known || typed;
  };

  const entry = resolveEntry();
  const isDuplicate =
    !!entry && values.some((value) => normalise(value) === normalise(entry));

  const handleAdd = () => {
    if (!entry || isDuplicate) return;
    onUpdate([...values, entry]);
    closeModal();
  };

  const handleRemove = (valueToRemove) => {
    onUpdate(values.filter((value) => value !== valueToRemove));
  };

  // Suggestions the coach has not taken yet, plus the way out of the list.
  const availableOptions = options.filter(
    (option) => !values.some((value) => normalise(value) === normalise(option))
  );

  return (
    <div className="bg-white p-6 border border-[#E6E6E6]">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-[11px] font-bold text-[#3C3C3C]">{title}</h2>
        {isEditing && (
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 border border-[#E6E6E6] bg-white text-[#1E1E1E] rounded-lg"
          >
            {addButtonLabel}
          </button>
        )}
      </div>

      <div className="space-y-4">
        {values.length === 0 ? (
          <div className="p-8 text-center border border-dashed border-gray-300 rounded-lg bg-[#F8F9FB]">
            <p className="text-sm text-[#646B7E]">{emptyLabel}</p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {values.map((value, index) => (
              <div
                key={index}
                className="px-4 py-2 bg-[#F8F9FB] border border-[#E6E6E6] rounded-lg flex items-center gap-2"
              >
                <span className="text-sm text-[#1A212F] font-medium">{value}</span>
                {isEditing && (
                  <button
                    onClick={() => handleRemove(value)}
                    aria-label={removeAriaLabel}
                    className="text-red-500 hover:text-red-700"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-[16px] font-bold text-[#3C3C3C]">{modalTitle}</h3>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{selectLabel}</label>
                <select
                  value={selectedValue}
                  onChange={(e) => setSelectedValue(e.target.value)}
                  className="w-full px-3 py-2 border border-[#D2D4D8] rounded-lg bg-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">{selectPlaceholder}</option>
                  {availableOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                  <option value={OTHER_OPTION_LABEL}>{OTHER_OPTION_LABEL}</option>
                </select>
              </div>

              {isOther && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ADD YOUR OWN</label>
                  <input
                    type="text"
                    value={customValue}
                    maxLength={TACTIC_TERM_MAX_LENGTH}
                    onChange={(e) => setCustomValue(e.target.value)}
                    placeholder="Add your own wording here"
                    className="w-full px-3 py-2 border border-[#D2D4D8] rounded-lg bg-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              )}

              {isDuplicate && (
                <p className="text-sm text-[#646B7E]">Already in the list</p>
              )}

              <div className="flex gap-2 justify-end">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 border border-[#E6E6E6] bg-white text-[#1E1E1E] rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdd}
                  disabled={!entry || isDuplicate}
                  className="px-4 py-2 bg-[#2283FF] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TacticSelectionSection;
